// src/utils/ohlcvNativeUtils.js

/**
 * Parses and processes OHLCV data from CoinGecko API for native tokens
 * @param {Array} ohlcvList - Raw OHLCV data from the API
 * @returns {Array} - Formatted OHLCV data ready for charting
 */
export const parseNativeOHLCVData = (ohlcvList) => {
    if (!ohlcvList || !Array.isArray(ohlcvList)) {
      return [];
    }
    
    return ohlcvList.map(item => ({
      timestamp: item[0], // CoinGecko already provides timestamp in milliseconds
      open: item[1],
      high: item[2],
      low: item[3],
      close: item[4],
      // No volume in CoinGecko's OHLC endpoint
    }));
  };
  
  /**
   * Calculates price change percentage
   * @param {Array} data - OHLCV data
   * @returns {number} - Price change percentage
   */
  export const calculateNativePriceChange = (data) => {
    if (!data || data.length < 2) {
      return 0;
    }
    
    const firstPrice = data[0].close || data[0].price;
    const lastPrice = data[data.length - 1].close || data[data.length - 1].price;
    
    return ((lastPrice - firstPrice) / firstPrice) * 100;
  };
  
  /**
   * Determines chart color based on price trend
   * @param {Array} data - OHLCV data
   * @returns {string} - CSS color variable or hex color
   */
  export const getNativeChartColor = (data) => {
    if (!data || data.length < 2) {
      return "var(--text-tertiary)";
    }
    
    const priceChange = calculateNativePriceChange(data);
    
    return priceChange >= 0 ? "var(--success)" : "var(--error)";
  };
  
  /**
   * Formats date for chart axis based on timeframe
   * @param {number} timestamp - Timestamp in milliseconds
   * @param {string} timeframe - Selected timeframe
   * @returns {string} - Formatted date/time string
   */
  export const formatNativeAxisDate = (timestamp, timeframe) => {
    const date = new Date(timestamp);
    
    switch (timeframe) {
      case '24h':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case '1w':
        return `${date.getMonth() + 1}/${date.getDate()}`;
      case '1m':
      case '3m':
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      default:
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };
  
  /**
   * Gets days parameter for CoinGecko API based on timeframe
   * @param {string} timeframe - Selected timeframe ('24h', '1w', '1m', '3m')
   * @returns {number} - Days parameter for API
   */
  export const getDaysParam = (timeframe) => {
    switch (timeframe) {
      case '24h': return 1;
      case '1w': return 7;
      case '1m': return 30;
      case '3m': return 90;
      default: return 1;
    }
  };
  
  /**
   * Creates the full CoinGecko API URL for OHLCV data
   * @param {string} coinId - CoinGecko coin ID (e.g., 'bitcoin', 'ethereum')
   * @param {string} timeframe - Selected timeframe ('24h', '1w', '1m', '3m')
   * @returns {string} - Complete API URL
   */
  export const buildNativeOHLCVApiUrl = (coinId, timeframe) => {
    const days = getDaysParam(timeframe);
    return `https://api.coingecko.com/api/v3/coins/${coinId}/ohlc?vs_currency=usd&days=${days}&precision=full`;
  };